<template>
  <div>
    <!-- Filtros -->
    <transition name="fade" mode="out-in">
      <b-card v-if="showFilters" no-body class="mb-2">
        <b-card-header class="d-flex justify-content-between pl-1 pt-1">
          <div class="d-flex align-items-center">
            <h5 class="mb-0">
              {{ $t('Filters') }}
            </h5>
            <b-badge v-if="hasActiveFilters" variant="primary" class="ml-2">
              {{ $t('Active') }}
            </b-badge>
          </div>
          <div class="d-flex align-items-center">
            <b-button
              v-if="hasActiveFilters"
              variant="outline-secondary"
              size="sm"
              @click="clearAllFilters"
              class="mr-2"
              v-b-tooltip.hover
              :title="$t('Clear filters')">
              <feather-icon icon="Trash2Icon" size="14"/>
            </b-button>
            <b-avatar
              @click="showFilters = false"
              v-b-tooltip.hover
              :title="$t('Close Filters')"
              class="mr-1"
              button
              variant="danger"
              size="30px">
              <feather-icon icon="XIcon"/>
            </b-avatar>
          </div>
        </b-card-header>
        <b-card-body>
          <b-row class="mb-1">
            <!-- Field: Owner -->
            <b-col cols="12" md="6" class="mb-md-0 mb-2">
              <h5 class="font-weight-bold">{{ $t('Owner') }}</h5>
              <vue-autosuggest
                v-model="searchOwner"
                :suggestions="filteredCustomerOptions"
                :input-props="inputProps"
                @selected="selectCustomer"
                @input="onInputChange">
                <template slot-scope="{suggestion}">
                  <span class="my-suggestion-item">{{ suggestion.item.name }}</span>
                  <div v-show="suggestion.item.ownerName">
                    <span class="text-muted">{{ $t('Belongs to: ') }}</span>
                    <span class="font-weight-bold"> {{ suggestion.item.ownerName }} </span>
                  </div>
                </template>
              </vue-autosuggest>
            </b-col>

            <!-- Field: Manufacturer -->
            <b-col cols="12" md="6">
              <h5 class="font-weight-bold">{{ $t('Search') }}</h5>
              <b-form-input
                  v-model="search"
                  
                  :placeholder="$t('Search ...')"
              />
            </b-col>
          </b-row>
          <b-row>
            <!-- Field: Manufacturer -->
            <b-col cols="12" md="4">
              <h5 class="font-weight-bold">{{ $t('Manufacturer') }}</h5>
              <v-select
                v-model="assetBrandId"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="manufacturerOptions"
                :reduce="val => val.value"
                :clearable="false"
                input-id="manufacturer"/>
            </b-col>
            <!-- Field: Asset Type -->
            <b-col cols="12" md="4" class="mb-md-0 mb-2">
              <h5 class="font-weight-bold">{{ $t('Asset Type') }}</h5>
              <v-select
                v-model="assetTypeId"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="assetTypeOptions"
                :reduce="val => val.value"
                :clearable="false"
                input-id="asset-type"/>
            </b-col>

            <!-- Field: Model -->
            <b-col cols="12" md="4">
              <h5 class="font-weight-bold">{{ $t('Model') }}</h5>
              <v-select
                v-model="assetModelId"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="modelOptions"
                :reduce="val => val.value"
                :clearable="false"
                :disabled="!assetBrandId"
                input-id="asset-model"/>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>
    </transition>

    <!-- Mapa -->
    <b-card no-body>
      <b-card-header class="d-flex justify-content-between align-items-center">
        <h5 class="mb-0">{{ $t('Assets Map') }}</h5>
        <div class="d-flex align-items-center">
          <b-button
            v-if="hasActiveFilters"
            class="mr-2"
            variant="outline-secondary"
            size="sm"
            @click="clearAllFilters"
            v-b-tooltip.hover
            :title="$t('Clear Filters')">
            <feather-icon icon="XIcon" size="14"/>
            {{ $t('Clear Filters') }}
          </b-button>
          <b-button
            v-show="!showFilters"
            class="btn-icon position-relative"
            @click="showFilters = true"
            :variant="hasActiveFilters ? 'success' : 'primary'"
            v-b-tooltip.hover
            :title="hasActiveFilters ? $t('Open Filters (Active)') : $t('Open Filters')">
            <feather-icon icon="FilterIcon"/>
            <b-badge
              v-if="hasActiveFilters"
              variant="warning"
              pill
              class="position-absolute badge-notification">
              !
            </b-badge>
          </b-button>
        </div>
      </b-card-header>
      <b-card-body class="position-relative">
        <!-- Loading State Overlay -->
<!--        <div v-if="loading" class="position-absolute w-100 d-flex align-items-center justify-content-center" style="z-index: 1000; background: rgba(255,255,255,0.9); top: 0; left: 0; height: 600px; border-radius: 0.357rem; display: none">-->
<!--          <div class="text-center">-->
<!--            <b-spinner variant="primary" label="Loading"></b-spinner>-->
<!--            <p class="mt-2 text-muted">{{ $t('Loading assets...') }}</p>-->
<!--          </div>-->
<!--        </div>-->

        <!-- Error State -->
        <b-alert v-if="error" variant="danger" show class="mb-2">
          <feather-icon icon="AlertCircleIcon" size="16" class="mr-1"/>
          {{ error }}
        </b-alert>

        <!-- Legenda + cercas (uma linha compacta) -->
        <div
          class="map-legend-toolbar mb-2 d-flex flex-wrap align-items-center justify-content-between"
          role="region"
          :aria-busy="fencesLoading">
          <div class="status-legend d-flex flex-column align-items-start mb-0 mr-2 flex-grow-1 min-w-0">
            <span class="small text-muted mb-25">{{ $t('Legend') }}:</span>
            <div class="d-flex flex-wrap align-items-center">
              <div
                v-for="item in statusLegend"
                :key="`legend-${item.key}`"
                class="d-flex align-items-center mr-2 mb-1">
                <span
                  class="legend-dot"
                  :class="`legend-dot-${item.key}`"/>
                <small class="ml-1">{{ item.label }}</small>
              </div>
            </div>
          </div>

          <div
            class="map-fences-inline d-flex align-items-center flex-shrink-0 mt-1 mt-sm-0"
            :aria-label="$t('Show virtual fences')">
            <div class="map-fences-pill d-flex align-items-center">
              <feather-icon
                icon="LayersIcon"
                size="14"
                class="map-fences-pill-icon flex-shrink-0"
                aria-hidden="true"/>
              <span class="map-fences-pill-label">
                {{ $t('Virtual fences short') }}
              </span>
              <span
                v-b-tooltip.hover.bottom
                class="map-fences-info d-inline-flex align-items-center ml-25"
                :title="$t('Show virtual fences help')"
                tabindex="0"
                role="button"
                :aria-label="$t('Show virtual fences help')">
                <feather-icon icon="InfoIcon" size="13" class="text-muted"/>
              </span>
              <span id="map-fences-sr-help" class="sr-only">{{ $t('Show virtual fences help') }}</span>
              <b-form-checkbox
                id="map-show-fences-checkbox"
                v-model="showFencesOnMap"
                switch
                class="mb-0 map-fences-switch"
                :disabled="fencesLoading"
                :aria-describedby="'map-fences-sr-help'"/>
              <b-spinner
                v-if="fencesLoading"
                small
                variant="primary"
                class="ml-50 flex-shrink-0"
                :label="$t('Loading fences')"/>
            </div>
          </div>
        </div>

        <!-- Map - sempre renderizado -->
        <div>
          <l-map
            ref="map"
            class="assets-map"
            :zoom="zoom"
            :center="center"
            :options="mapOptions"
            @ready="handleMapReady">
            <!-- Layers Control -->
            <l-control-layers position="topright" :collapsed="true"></l-control-layers>

            <!-- Tile Layers -->
            <l-tile-layer
              :name="$t('Map')"
              :url="'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'"
              layer-type="base"
              :checked="defaultLayer === 'map'"></l-tile-layer>

            <!-- ArcGIS Híbrido (Imagem de Satélite + Labels de Ruas) -->
            <l-tile-layer
              :name="$t('Satellite')"
              :url="arcgisSatelliteUrl"
              layer-type="base"
              :checked="defaultLayer === 'satellite'"></l-tile-layer>

            <l-tile-layer
              :name="$t('Show locations')"
              :url="arcgisLabelsUrl"
              layer-type="overlay"
              :checked="showLocalities"></l-tile-layer>

            <l-tile-layer
              :name="$t('Show street names')"
              :url="arcgisRoadsUrl"
              layer-type="overlay"
              :checked="showRoads"></l-tile-layer>

            <!-- Clusters -->
            <l-marker
              v-for="cluster in clusters"
              :key="`cluster-${cluster.lat}-${cluster.lng}`"
              :lat-lng="getClusterPosition(cluster)"
              :z-index-offset="0">
              <l-icon
                :class-name="`cluster-marker ${viewMode === 'icon' ? 'cluster-small' : ''}`"
                :icon-size="viewMode === 'icon' ? [36, 36] : [48, 48]"
                :icon-anchor="viewMode === 'icon' ? [18, 18] : [24, 24]">
                <div class="cluster-icon">
                  <span class="cluster-count">{{ cluster.count || 0 }}</span>
                </div>
              </l-icon>
              <l-popup>
                <div class="cluster-popup">
                  <div class="d-flex align-items-center mb-2">
                    <feather-icon icon="LayersIcon" size="18" class="mr-1 text-primary"/>
                    <h6 class="font-weight-bold mb-0 text-primary">
                      {{ $t('Cluster') }}
                    </h6>
                  </div>
                  <div class="cluster-info">
                    <b-badge variant="primary" pill class="mb-2">
                      {{ cluster.count || 0 }} {{ $t('assets') }}
                    </b-badge>
                    <p class="text-muted mb-0 small">
                      {{ $t('Zoom in to see individual assets') }}
                    </p>
                  </div>
                </div>
              </l-popup>
              <l-tooltip>
                <div class="text-center">
                  <strong class="text-primary">{{ $t('Cluster') }}</strong><br>
                  <small>{{ cluster.count || 0 }} {{ $t('assets') }}</small>
                </div>
              </l-tooltip>
            </l-marker>

            <!-- Markers dos ativos -->
            <l-marker
              v-for="asset in assetsWithValidCoordinates"
              :key="`marker-${asset.assetId || asset.id}-${asset.lastLat}-${asset.lastLng}`"
              :lat-lng="getAssetPosition(asset)"
              :z-index-offset="getAssetZIndex(asset)">
              <!-- Visualização com ícone -->
              <l-icon
                v-if="viewMode === 'icon'"
                :class-name="`asset-marker asset-${asset.lastWorkstatus || 'noconnection'}`"
                :icon-size="[24, 24]"
                :icon-anchor="[12, 24]">
                <img
                  class="asset-icon asset-icon-small"
                  :src="getAssetIcon(asset)"
                  :alt="asset.plate || asset.chassisNumber || asset.nickname"
                  @error="(e) => handleImageError(e, asset)">
              </l-icon>
              <!-- Visualização com ponto -->
              <l-icon
                v-else
                :class-name="`asset-point asset-point-${asset.lastWorkstatus || 'noconnection'}`"
                :icon-size="[16, 16]"
                :icon-anchor="[8, 8]">
                <div class="asset-point-marker"></div>
              </l-icon>
              <l-popup>
                <div class="asset-popup">
                  <div class="asset-popup-icon-wrap mb-2">
                    <img
                      class="asset-popup-icon"
                      :src="getAssetIcon(asset)"
                      :alt="asset.plate || asset.chassisNumber || asset.nickname"
                      @error="(e) => handleImageError(e, asset)">
                  </div>
                  <p class="mb-1">
                    <small>
                      <template v-if="asset.plate">
                        <strong>{{ $t('Plate') }}:</strong> {{ asset.plate }}<br>
                      </template>
                      <template v-if="asset.chassisNumber">
                        <strong>{{ $t('Chassis') }}:</strong> {{ asset.chassisNumber }}<br>
                      </template>
                      <template v-if="asset.nickname">
                        <strong>{{ $t('Nickname') }}:</strong> {{ asset.nickname }}<br>
                      </template>
                      <strong>{{ $t('Status') }}:</strong>
                      <b-badge :variant="getStatusVariant(asset.lastWorkstatus)">
                        {{ getStatusText(asset.lastWorkstatus) }}
                      </b-badge>
                    </small>
                  </p>
                  <b-button
                    v-if="asset.assetId || asset.id"
                    size="sm"
                    variant="primary"
                    :to="{ name: 'apps-asset-summary', params: { id: asset.assetId || asset.id } }">
                    <feather-icon icon="ArrowRightIcon" size="14" class="mr-1"/>
                    {{ $t('View Details') }}
                  </b-button>
                </div>
              </l-popup>
            </l-marker>
            <!-- Cercas virtuais - polígonos (dados só quando "Mostrar cercas" está ativo) -->
            <l-polygon
              v-for="fence in polygonFences"
              :key="`fence-polygon-${fence.id}`"
              :lat-lngs="fence.points">
              <l-popup>
                <b-row>
                  <b-col cols="12">
                    <popup-fence
                      :isFence="true"
                      :myId="fence.id"
                      :name="fence.name"
                      :maxSpeed="fence.maxSpeed"
                      :alertIn="fence.alertIn"
                      :alertOut="fence.alertOut"
                      :alert-stay-in="fence.alertStayIn"
                      :alert-stay-out="fence.alertStayOut"
                      :virtualFenceTypeName="fence.virtualFenceTypeName"
                      :hideDelete="true"
                      @event-to-fence="$router.push({ name: 'fence-register', params: { id: fence.id } })"
                      @event-delete-fence="() => {}"
                    />
                  </b-col>
                </b-row>
              </l-popup>
            </l-polygon>

            <!-- Cercas virtuais - círculos -->
            <l-circle
              v-for="fence in circleFences"
              :key="`fence-circle-${fence.id}`"
              :lat-lng="[fence.lat, fence.lng]"
              :radius="fence.radius">
              <l-popup>
                <b-row>
                  <b-col cols="12">
                    <popup-fence
                      :isFence="true"
                      :myId="fence.id"
                      :name="fence.name"
                      :maxSpeed="fence.maxSpeed"
                      :alertIn="fence.alertIn"
                      :alertOut="fence.alertOut"
                      :alert-stay-in="fence.alertStayIn"
                      :alert-stay-out="fence.alertStayOut"
                      :virtualFenceTypeName="fence.virtualFenceTypeName"
                      :hideDelete="true"
                      @event-to-fence="$router.push({ name: 'fence-register', params: { id: fence.id } })"
                      @event-delete-fence="() => {}"
                    />
                  </b-col>
                </b-row>
              </l-popup>
            </l-circle>
          </l-map>

          <!-- Info sobre quantidade de ativos -->
          <div class="mt-2 text-muted text-center">
            <small>
              <template v-if="useViewPortMode">
                {{ $t('Showing') }} {{ assetsWithValidCoordinates.length }} {{ $t('assets') }}
                <template v-if="clusters.length > 0">
                  {{ $t('and') }} {{ clusters.length }} {{ $t('clusters') }}
                </template>
                {{ $t('in current view') }}
                ({{ $t('Total') }}: {{ totalAssets }})
              </template>
              <template v-else>
                {{ $t('Showing') }} {{ assetsWithValidCoordinates.length }} {{ $t('assets with valid coordinates') }}
                {{ $t('of') }} {{ totalAssets }} {{ $t('total') }}
              </template>
            </small>
          </div>
        </div>
      </b-card-body>
    </b-card>

  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BButton,
  BButtonGroup,
  BAvatar,
  BBadge,
  BRow,
  BCol,
  BSpinner,
  BAlert,
  VBTooltip,
  BFormInput,
  BFormCheckbox
} from 'bootstrap-vue'
import {
  LMap,
  LTileLayer,
  LMarker,
  LIcon,
  LPopup,
  LTooltip,
  LControlLayers,
  LControl,
  LPolygon,
  LCircle
} from 'vue2-leaflet'
import PopupFence from '@/views/main/components/PopupFence.vue'
import 'leaflet/dist/leaflet.css'
import {ref, computed, onMounted, nextTick, getCurrentInstance, onUnmounted, watch} from '@vue/composition-api'
import vSelect from 'vue-select'
import { VueAutosuggest } from 'vue-autosuggest'
import useMap from './useMap'

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BButton,
    BButtonGroup,
    BAvatar,
    BBadge,
    BRow,
    BCol,
    BSpinner,
    BAlert,
    LMap,
    LTileLayer,
    LMarker,
    LIcon,
    LPopup,
    LTooltip,
    LControlLayers,
    LControl,
    LPolygon,
    LCircle,
    PopupFence,
    vSelect,
    BFormInput,
    BFormCheckbox,
    VueAutosuggest
  },
  directives: {
    'b-tooltip': VBTooltip
  },
  setup(props, { root }) {
    const {
      // Estado do mapa
      mapReady,
      center,
      zoom,
      mapOptions,
      defaultLayer,
      showLocalities,
      showRoads,
      showFencesOnMap,
      fencesLoading,
      arcgisSatelliteUrl,
      arcgisLabelsUrl,
      arcgisRoadsUrl,
      onMapReady: onMapReadyHandler,

      // Estado dos ativos
      assets,
      clusters,
      assetsWithValidCoordinates,
      loading,
      error,
      totalAssets,
      useViewPortMode,
      // Cercas virtuais
      circleFences,
      polygonFences,

      // Filtros
      customerId,
      assetTypeId,
      assetBrandId,
      assetModelId,
      showFilters,
      searchOwner,
      hasActiveFilters,
      viewMode,

      // Opções dos filtros
      manufacturerOptions,
      assetTypeOptions,
      modelOptions,
      filteredCustomerOptions,
      search,

      // Funções
      initializeAssets,
      clearAllFilters,
      selectCustomer,
      getAssetIcon,
      getDefaultAssetIcon,
      getAssetPosition,
      getClusterPosition,
      updateAssetFromTlm,
      fetchAndDisplayAssetFromTlm
    } = useMap()

    // Props para autosuggest
    const inputProps = {
      id: 'autosuggest__input',
      class: 'form-control',
      placeholder: root.$t('Search owner ...')
    }

    // Função para lidar com mudanças no input de busca de cliente
    const onInputChange = (text) => {
      searchOwner.value = text || ''
    }

    // Função para lidar com erro ao carregar imagem (ex.: URL quebrada) — usa ícone padrão
    const handleImageError = (e, asset) => {
      if (asset) {
        e.target.src = getDefaultAssetIcon(asset)
      }
    }

    // Função para obter variante do status
    const getStatusVariant = (status) => {
      if (status === 'failure') return 'danger'
      if (status === 'off') return 'secondary'
      if (status === 'noconnection' || status === '' || status === 'neverconnected') return 'light-secondary'
      if (status === 'severe') return 'warning'
      if (status === 'iddle') return 'info'
      if (status === 'on') return 'success'
      return 'light-secondary'
    }

    // Função para obter texto do status
    const getStatusText = (status) => {
      if (!status || status === '') return root.$t('Never Connected')
      const statusLower = status.toLowerCase()
      const statusMap = {
        'on': root.$t('on'),
        'off': root.$t('off'),
        'failure': root.$t('failure'),
        'noconnection': root.$t('noconnection'),
        'neverconnected': root.$t('Never Connected'),
        'severe': root.$t('severe'),
        'iddle': root.$t('iddle')
      }
      return statusMap[statusLower] || status
    }

    // Legenda dos status de trabalho
    const statusLegend = [
      { key: 'on', label: root.$t('on') },
      { key: 'iddle', label: root.$t('iddle') },
      { key: 'off', label: root.$t('off') },
      { key: 'failure', label: root.$t('failure') },
      { key: 'severe', label: root.$t('high load') },
      { key: 'noconnection', label: root.$t('noconnection') },
    ]

    // Função para obter z-index do ativo baseado no status
    const getAssetZIndex = (asset) => {
      const status = (asset.lastWorkstatus || '').toLowerCase()
      // Ativos com severe ou failure ficam por cima (z-index maior)
      if (status === 'severe' || status === 'failure') {
        return 1000 // Z-index alto para ficar sobreposto
      }
      // Demais ativos ficam acima dos clusters mas abaixo dos severe/failure
      return 500
    }

    // Função para injetar opções de visualização no controle de camadas
    const injectViewModeControl = () => {
      nextTick(() => {
        // Aguardar um pouco para garantir que o controle de camadas foi renderizado
        setTimeout(() => {
          const layersControl = document.querySelector('.leaflet-control-layers')
          if (layersControl && !layersControl.querySelector('.view-mode-section')) {
            // Criar seção de visualização
            const viewModeSection = document.createElement('div')
            viewModeSection.className = 'view-mode-section'
            viewModeSection.innerHTML = `
              
              <div class="view-mode-label">${root.$t('View Mode')}</div>
              <div class="view-mode-buttons">
                <button class="view-mode-btn ${viewMode.value === 'icon' ? 'active' : ''}" data-mode="icon" title="${root.$t('Icon View')}">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                    <polyline points="10 9 9 9 8 9"></polyline>
                  </svg>
                </button>
                <button class="view-mode-btn ${viewMode.value === 'point' ? 'active' : ''}" data-mode="point" title="${root.$t('Point View')}">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                  </svg>
                </button>
              </div>
            `
            
            // Adicionar event listeners
            viewModeSection.querySelectorAll('.view-mode-btn').forEach(btn => {
              btn.addEventListener('click', (e) => {
                e.preventDefault()
                e.stopPropagation()
                const mode = btn.getAttribute('data-mode')
                viewMode.value = mode
                updateViewModeButtons()
              })
            })
            
            // Adicionar ao controle de camadas
            layersControl.appendChild(viewModeSection)
          }
        }, 300)
      })
    }

    // Função para atualizar os botões de visualização
    const updateViewModeButtons = () => {
      const buttons = document.querySelectorAll('.view-mode-btn')
      buttons.forEach(btn => {
        const mode = btn.getAttribute('data-mode')
        if (mode === viewMode.value) {
          btn.classList.add('active')
        } else {
          btn.classList.remove('active')
        }
      })
    }

    // Watch para atualizar botões quando viewMode mudar
    watch(viewMode, () => {
      updateViewModeButtons()
    })

    // Função para lidar com o evento ready do mapa
    const handleMapReady = (map) => {
      onMapReadyHandler(map)
      // Injetar controle de visualização após o mapa estar pronto
      injectViewModeControl()
    }
    const registerEventBusListeners = () => {
      root.$on("tlm", event_tlm);
    }
    
    const deregisterEventBusListeners = () =>{
      root.$off("tlm", event_tlm);
    }

    const event_tlm = async (data) => {
      try {
        if (!data) return

        const workStatus = (data.workStatus ?? data.workstatus ?? '').toString().toLowerCase()
        const isSevereOrFailure = workStatus === 'severe' || workStatus === 'failure'

        // Atualizar asset se já estiver na lista
        const result = updateAssetFromTlm(data)

        // Se status severe/failure e ativo não está sendo exibido, buscar summary e exibir no mapa
        if (isSevereOrFailure && !result.found) {
          await fetchAndDisplayAssetFromTlm(data)
        }
      } catch (_) {
        // falha silenciosa no evento em tempo real
      }
    }

    onMounted(() => {
      const instance = getCurrentInstance()
      registerEventBusListeners()
      nextTick(() => {
        setTimeout(() => {
          if (instance && instance.refs && instance.refs.map) {
            const mapComponent = instance.refs.map
            if (mapComponent.mapObject && !mapReady.value) {
              onMapReadyHandler(mapComponent)
              // Injetar controle de visualização
              injectViewModeControl()
            }
          }
        }, 500) // Aguardar 500ms para garantir que o Leaflet inicializou
      })
    })
    
       
    onUnmounted(() => {
      deregisterEventBusListeners()
    })

    return {
      // Estado do mapa
      mapReady,
      center,
      zoom,
      mapOptions,
      defaultLayer,
      showLocalities,
      showRoads,
      showFencesOnMap,
      fencesLoading,
      arcgisSatelliteUrl,
      arcgisLabelsUrl,
      arcgisRoadsUrl,
      handleMapReady,

      // Estado dos ativos
      assets,
      clusters,
      assetsWithValidCoordinates,
      loading,
      error,
      totalAssets,
      useViewPortMode,

      // Filtros
      customerId,
      assetTypeId,
      assetBrandId,
      assetModelId,
      showFilters,
      searchOwner,
      hasActiveFilters,
      viewMode,
      search,

      // Opções dos filtros
      manufacturerOptions,
      assetTypeOptions,
      modelOptions,
      filteredCustomerOptions,
      inputProps,

      // Funções
      initializeAssets,
      clearAllFilters,
      selectCustomer,
      getAssetIcon,
      getAssetPosition,
      getClusterPosition,
      onInputChange,
      handleImageError,
      getStatusVariant,
      getStatusText,
      statusLegend,
      getAssetZIndex,
      registerEventBusListeners,
      deregisterEventBusListeners,
      event_tlm,

      // Cercas virtuais
      circleFences,
      polygonFences,
    }
  }
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/map-leaflet.scss';
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-autosuggest.scss';

.assets-map {
  height: 600px;
  width: 100%;
  border-radius: 0.357rem;
  overflow: hidden;
}

// Garantir que o Leaflet seja exibido corretamente
.vue2leaflet-map.leaflet-container {
  height: 600px !important;
  width: 100% !important;
}

.assets-map.leaflet-container {
  height: 600px !important;
  width: 100% !important;
}

.asset-marker {
  padding: 4px;
  border-radius: 50px;
  box-shadow: 5px 3px 10px rgba(0, 0, 0, 0.2);
  text-align: center;
  width: auto !important;
  height: auto !important;
}

// Cores de fundo baseadas no status do ativo
.asset-on {
  background-color: #28c76f !important;
}

.asset-off {
  background-color: #82868b !important;
}

.asset-failure {
  background-color: #ef5258 !important;
}

.asset-noconnection {
  background-color: #f5f4f8 !important;
}

.asset-severe {
  background-color: #ff9d54 !important;
}

.asset-iddle {
  background-color: #00cfe8 !important;
}

.asset-neverconnected {
  background-color: #f5f4f8 !important;
}

// Legenda de status de trabalho
.status-legend {
  font-size: 0.75rem;
}

// Toolbar legenda + cercas (compacto)
.map-legend-toolbar {
  gap: 0.5rem 1rem;
}

.map-fences-pill {
  padding: 0.2rem 0.5rem 0.2rem 0.35rem;
  border-radius: 2rem;
  background: linear-gradient(135deg, rgba(115, 103, 240, 0.08) 0%, rgba(115, 103, 240, 0.02) 100%);
  border: 1px solid rgba(115, 103, 240, 0.18);
  box-shadow: 0 1px 2px rgba(34, 41, 47, 0.06);
}

.map-fences-pill-icon {
  color: #7367f0;
  margin-right: 0.35rem;
  opacity: 0.9;
}

.map-fences-pill-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #5e5873;
  letter-spacing: 0.01em;
  white-space: nowrap;
  margin-right: 0.15rem;
}

.map-fences-info {
  cursor: help;
  padding: 0.1rem;
  border-radius: 50%;
  line-height: 0;
  transition: background-color 0.15s ease;

  &:hover,
  &:focus {
    background-color: rgba(115, 103, 240, 0.12);
    outline: none;
  }
}

.map-fences-switch {
  padding-left: 0.35rem;

  ::v-deep .custom-control-label::before {
    width: 1.85rem;
    height: 1rem;
  }

  ::v-deep .custom-control-label::after {
    width: calc(1rem - 4px);
    height: calc(1rem - 4px);
  }

  ::v-deep .custom-control-input:checked ~ .custom-control-label::before {
    border-color: #7367f0;
    background-color: #7367f0;
  }
}


.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 1px solid #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

.legend-dot-on {
  background-color: #28c76f;
}

.legend-dot-off {
  background-color: #82868b;
}

.legend-dot-failure {
  background-color: #ef5258;
}

.legend-dot-noconnection {
  background-color: #f5f4f8;
  border-color: #d3d3d3;
}

.legend-dot-severe {
  background-color: #ff9d54;
}

.legend-dot-iddle {
  background-color: #00cfe8;
}

.legend-dot-neverconnected {
  background-color: #f5f4f8;
  border-color: #d3d3d3;
}

// Estilos para clusters - Diferenciação visual clara
.cluster-marker {
  text-align: center;
  width: auto !important;
  height: auto !important;
  cursor: pointer;
}

.cluster-icon {
  width: 48px;
  height: 48px;
  min-width: 48px;
  min-height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
  border: 4px solid #ffffff;
  box-shadow:
      0 4px 12px rgba(30, 111, 123, 0.4),
      0 0 0 2px rgba(30, 111, 123, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-weight: 700;
  font-size: 15px;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    transform: scale(1.1);
    box-shadow:
        0 6px 16px rgba(30, 111, 123, 0.5),
        0 0 0 3px rgba(30, 111, 123, 0.25);
  }
}

// Estilos para clusters menores no modo ícone
.cluster-small .cluster-icon {
  width: 36px;
  height: 36px;
  min-width: 36px;
  min-height: 36px;
  border: 3px solid #ffffff;
  font-size: 12px;
}


.cluster-count {
  color: #fff;
  font-weight: 700;
  font-size: 16px;
  line-height: 1;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.cluster-small .cluster-count {
  font-size: 12px;
}

.cluster-popup {
  min-width: 200px;
  text-align: center;
  padding: 4px 0;
  
  h6 {
    font-size: 1rem;
    color: #7367f0;
  }
  
  .cluster-info {
    display: flex;
    flex-direction: column;
    align-items: center;
    
    .badge {
      font-size: 1.1rem;
      padding: 0.5rem 1rem;
    }
  }
  
  small {
    font-size: 0.75rem;
    line-height: 1.4;
  }
}

.asset-icon {
  width: 28px;
  height: 28px;
  object-fit: contain;
}

.asset-icon-small {
  width: 20px;
  height: 20px;
}

// Estilos para visualização com pontos
.asset-point {
  text-align: center;
  width: auto !important;
  height: auto !important;
}

.asset-point-marker {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  transition: all 0.2s ease;
  
  &:hover {
    transform: scale(1.3);
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.4);
  }
}

// Cores dos pontos baseadas no status
.asset-point-on .asset-point-marker {
  background-color: #28c76f;
}

.asset-point-off .asset-point-marker {
  background-color: #82868b;
}

.asset-point-failure .asset-point-marker {
  background-color: #ef5258;
}

.asset-point-noconnection .asset-point-marker {
  background-color: #f5f4f8;
  border-color: #d3d3d3;
}

.asset-point-severe .asset-point-marker {
  background-color: #ff9d54;
}

.asset-point-iddle .asset-point-marker {
  background-color: #00cfe8;
}

.asset-point-neverconnected .asset-point-marker {
  background-color: #f5f4f8;
  border-color: #d3d3d3;
}

.asset-popup {
  min-width: 200px;

  .asset-popup-icon-wrap {
    display: flex;
    justify-content: center;
  }

  .asset-popup-icon {
    width: 72px;
    height: 72px;
    object-fit: contain;
  }
  
  small {
    font-size: 0.875rem;
    line-height: 1.5;
  }
  
  // Melhorar contraste da badge light-secondary (sem conexão)
  .badge-light-secondary {
    background-color: #e9ecef !important;
    color: #495057 !important;
    border: 1px solid #adb5bd !important;
    font-weight: 600;
  }
}

.badge-notification {
  top: -5px;
  right: -5px;
  font-size: 0.6rem;
  min-width: 16px;
  height: 16px;
  line-height: 14px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

// Desabilitar hover do controle de camadas - mostrar apenas o ícone
.leaflet-control-layers {
  // Desabilitar expansão automática no hover
  &:not(.leaflet-control-layers-expanded):hover {
    .leaflet-control-layers-list {
      display: none !important;
    }
  }
  
  // Ocultar lista por padrão (quando collapsed)
  &:not(.leaflet-control-layers-expanded) {
    .leaflet-control-layers-list {
      display: none !important;
    }
    
    // Ocultar seção de visualização quando collapsed
    .view-mode-section {
      display: none !important;
    }
  }
  
  // Mostrar apenas quando expandido (clicado)
  &.leaflet-control-layers-expanded {
    .leaflet-control-layers-list {
      display: block !important;
    }
    
    // Mostrar seção de visualização quando expandido
    .view-mode-section {
      display: block !important;
    }
  }
}

// Estilos para opções de visualização dentro do controle de camadas
.leaflet-control-layers .view-mode-section {
  padding: 8px 10px;
  border-top: 1px solid #e0e0e0;
  margin-top: 4px;
  display: none; // Oculto por padrão
}

.view-mode-divider {
  height: 1px;
  background: #e0e0e0;
  margin: 4px 0 8px 0;
}

.view-mode-label {
  font-size: 11px;
  font-weight: 600;
  color: #5e5873;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
}

.view-mode-buttons {
  display: flex;
  gap: 6px;
  margin-top: 4px;
}

.view-mode-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 28px;
  border: 1px solid #d3d3d3;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;
  
  &:hover {
    background: #f5f5f5;
    border-color: #7367f0;
  }
  
  &.active {
    background: #7367f0;
    border-color: #7367f0;
    color: white;
    
    svg {
      color: white;
      stroke: white;
    }
    
    &:hover {
      background: #5e50e5;
      border-color: #5e50e5;
    }
  }
  
  svg {
    color: #5e5873;
    stroke: #5e5873;
    transition: all 0.2s ease;
  }
}

</style>
